/**
 * Post-processes a PptxGenJS-generated .pptx to add animation timing.
 *
 * PptxGenJS cannot emit <p:timing>, so the file is reopened as a zip, each slide part is
 * matched to its source slide, and a timing block referencing the shape ids PptxGenJS
 * assigned is appended. Shapes were tagged with an `objectName` token during export; that
 * token is the only reliable bridge between our object ids and the generated XML.
 */

import JSZip from 'jszip';
import { buildTimingXml, mapShapeIds, injectTiming, animObjectName } from './pptxAnimationXml';
import { TARGET_TYPE_COLLECTION } from './animationUtils';

/** Slide parts are ppt/slides/slide1.xml, slide2.xml, … in the order they were added. */
const slidePartName = (index) => `ppt/slides/slide${index + 1}.xml`;

/**
 * Every objectName token a step covers.
 * A SmartArt animated per node maps to that node's shape; animated as one object it maps to
 * all of its node shapes, since export flattens the diagram.
 */
const tokensForStep = (step, slideData) => {
  if (step.subTargetIds && step.subTargetIds.length) {
    return step.subTargetIds.map((nodeId) => animObjectName(step.targetId, nodeId));
  }
  if (step.targetType === 'smartart') {
    const collection = TARGET_TYPE_COLLECTION.smartart;
    const nodes = slideData?.[collection]?.[step.targetId]?.nodes || [];
    if (nodes.length) return nodes.map((n) => animObjectName(step.targetId, n.id));
  }
  return [animObjectName(step.targetId)];
};

/**
 * @param {Blob} pptxBlob   the file PptxGenJS produced
 * @param {Array} slideList [{ slideId, slideData }] in the same order they were exported
 * @returns {Promise<Blob>} the same file with timing added; the input blob if nothing applies
 */
export const injectAnimations = async (pptxBlob, slideList) => {
  const hasAnimations = slideList.some(
    ({ slideData }) =>
      slideData?.animations?.steps?.length || slideData?.animations?.passthrough?.length
  );
  if (!hasAnimations) return pptxBlob;

  const zip = await JSZip.loadAsync(pptxBlob);
  let changed = false;

  for (let i = 0; i < slideList.length; i += 1) {
    const { slideData } = slideList[i];
    const animations = slideData?.animations;
    if (!animations?.steps?.length && !animations?.passthrough?.length) continue;

    const partName = slidePartName(i);
    const part = zip.file(partName);
    if (!part) continue;

    // eslint-disable-next-line no-await-in-loop
    const slideXml = await part.async('string');
    const idByToken = mapShapeIds(slideXml);

    const resolveSpids = (step) =>
      tokensForStep(step, slideData)
        .map((token) => idByToken[token])
        .filter(Boolean);

    const timingXml = buildTimingXml(animations, slideData, resolveSpids);
    if (!timingXml) continue;

    zip.file(partName, injectTiming(slideXml, timingXml));
    changed = true;
  }

  if (!changed) return pptxBlob;
  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    compression: 'DEFLATE',
  });
};

export default injectAnimations;
